import { useCallback, useContext, useMemo, useState } from 'react';

import { CRM_CLIENTS_PAGE } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import {
  GUIDED_TOUR_FIRST_CLIENT,
  GUIDED_TOUR_PARAM,
} from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import { CRM_CLIENTS_TOUR_KEY } from 'src/features/crm/coachmark/stepHandlers';
import { useIsStudioUser } from 'src/hooks/useIsStudioUser';
import useNavigate from 'src/hooks/useNavigate';
import { useStudioTaskStatuses } from 'src/hooks/useStudioTaskStatuses';

const DISMISSED_STORAGE_KEY_PREFIX = 'portalInterceptModal.dismissed';

// Scope the flag by portal so a dismissal in one workspace doesn't hide the
// intercept in another.
const buildStorageKey = (portalId: string): string =>
  `${DISMISSED_STORAGE_KEY_PREFIX}.${portalId}`;

export interface UsePortalInterceptModalResult {
  /** True when the sidebar shortcut should open the modal instead of the portal. */
  shouldIntercept: boolean;
  isOpen: boolean;
  open: () => void;
  /** Persist the "seen" flag and close — used by the close button and the CTA. */
  dismiss: () => void;
  /** Route into the CRM guided flow, then dismiss. */
  goToCrm: () => void;
}

/**
 * Gates and drives the Studio FTUX "before you enter the portal" intercept. The
 * sidebar "Open Portal" shortcut skips the guided flow (meet the test client →
 * open the portal as them via the CRM); when the user hasn't completed that flow
 * yet, we interrupt with a modal that redirects them back into it. Dismissal is
 * remembered in localStorage so it only ever interrupts once.
 */
export const usePortalInterceptModal = (): UsePortalInterceptModalResult => {
  const isStudioUser = useIsStudioUser();
  const { completed: exploreCompleted } =
    useStudioTaskStatuses(CRM_CLIENTS_TOUR_KEY);
  const { navigate } = useNavigate();
  const portalConfig = useContext(PortalConfigContext);
  const storageKey = buildStorageKey(portalConfig.portalHeader);

  // Track a same-session dismissal separately, and re-read the persisted flag
  // whenever the key changes. The portal header starts empty and populates
  // asynchronously, so a lazy `useState` initializer would lock onto the wrong
  // portal-agnostic key at mount and never pick up the real per-portal value.
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const persistedDismissed = useMemo(
    () => localStorage.getItem(storageKey) === 'true',
    [storageKey],
  );
  const dismissed = dismissedThisSession || persistedDismissed;
  const [isOpen, setIsOpen] = useState(false);

  const shouldIntercept = isStudioUser && !exploreCompleted && !dismissed;

  const open = useCallback(() => setIsOpen(true), []);

  const dismiss = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setDismissedThisSession(true);
    setIsOpen(false);
  }, [storageKey]);

  const goToCrm = useCallback(() => {
    navigate(
      `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CLIENT}`,
    );
    dismiss();
  }, [navigate, dismiss]);

  return { shouldIntercept, isOpen, open, dismiss, goToCrm };
};
